import { useQuery } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { EnrollStudent } from '../components/EnrollStudent';
import { Modal } from '../components/Modal';
import { DaySelector } from '../components/DaySelector';
import { Program, getProgramTypeIcon, getScheduleTimeRange, getEnrollmentPaymentStatus, Invoice as ProgramInvoice, findProgramBySlug, isEnrollmentLocked } from '../types/program';
import { getStatusColor } from '../types/status';
import { EligibleRule } from '../types/discounts';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect, useMemo } from 'react';
import { formatDateDisplay, formatWeekdayDateDisplay, getTodayDateString } from '../utils/date';
import { v4 as uuidv4 } from 'uuid';
import { trackEvent } from '../utils/analytics';
import { isUUID, looksLikeSlug } from '../utils/uuid';
import { Info } from 'lucide-react';
import { BrandBadge } from './BrandBadge';
import { LocationPill } from './LocationPill';
import { PROGRAM_LOCATION_EMBED, programLocationName } from '../types/location';
import { useBrandScopedPage } from '../hooks/useBrandScopedPage';
import { useLandingBrand } from '../hooks/useLandingBrand';
import { getProgramTypeColors } from '../utils/billingDisplay';
import { isFlexDayCounted } from '../utils/flexPlan';
import { CampPaymentNudge } from './CampPaymentNudge';
import { useUnpaidInvoiceNudge } from '../hooks/useUnpaidInvoiceNudge';
import { ProgramScheduleSummary } from './ProgramScheduleSummary';
import { sessionDayPredicate } from '../utils/programSessionDates';
import { FirstClassPicker } from './FirstClassPicker';
import {
  businessNow,
  classShare,
  defaultFirstClass,
  describeProration,
  firstClassOptions,
  isBilledByClass,
  prorateAmount,
  termClasses,
  type BusinessNow,
} from '../utils/lateJoinProration';
import { fetchDiscountEligibility, findEligibilityDiscount, isEligibilityDiscountSubtype, resolveDiscountVisibility } from '../utils/eligibilityDiscounts';
import { SUMMER_CAMP_POLICY, getSummerCampTag, isSummerCampProgram } from '../utils/summerCampPolicy';
import { useLabels, useProgramTypeName } from '../config/useLabels';
import { useProgramSeats } from '../hooks/useProgramSeats';
import { ProgramWaitlist } from './ProgramWaitlist';
import { useCartHeldInvoiceIds } from '../hooks/useCartHeldInvoiceIds';

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  active: boolean;
  metadata?: {
    school?: string;
    [key: string]: unknown;
  };
}

// Minimal program data needed for slug lookup
interface ProgramForSlug {
  id: string;
  name: string;
}

interface Enrollment {
  id: string;
  student_id: string;
  program_id: string;
  created_at: string;
  updated_at: string;
  students: Student;
  invoices: ProgramInvoice[];
  enrollment_start_date: string;
  enrollment_end_date: string;
  is_active: boolean;
}

interface ProgramWithEnrollments extends Program {
  enrollments: Enrollment[];
}

interface LineItemConfig {
  id: string;
  name: string;
  type?: string;
  description?: string;
  subtype?: string;
}

interface PricingItem {
  amount: number;
  is_optional: boolean;
  line_item_config: LineItemConfig;
}

interface CampSessionProgram {
  id: string;
  name: string;
  billing_type: string;
  start_date: string;
  end_date: string;
  schedule_cron: string;
  pricing: PricingItem[];
}

interface CampSelectionState {
  selected: boolean;
  lunchSelected: boolean;
}

interface StudentCampEnrollmentRow {
  program_id: string;
}

const EMPTY_STUDENT_CAMP_ENROLLMENTS: StudentCampEnrollmentRow[] = [];

function areCampSelectionsEqual(
  left: Record<string, CampSelectionState>,
  right: Record<string, CampSelectionState>
): boolean {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;

  return leftKeys.every((key) => {
    const leftValue = left[key];
    const rightValue = right[key];
    return Boolean(rightValue)
      && leftValue.selected === rightValue.selected
      && leftValue.lunchSelected === rightValue.lunchSelected;
  });
}

type DatabasePricingRow = {
  amount: number;
  is_optional: boolean;
  line_item_configs: {
    id: string;
    name: string;
    type: string;
    description: string;
    subtype: string | null;
  }[];
}

// EligibleRule now imported from shared types

interface StudentParentRow {
  students: Student[];
}

export default function ProgramDetails() {
  // Invoices a bank bundle payment is still debiting: no payments row yet, but money is on its way.
  const cartHeldInvoiceIds = useCartHeldInvoiceIds();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const t = useLabels();
  const programTypeName = useProgramTypeName();
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [enrollmentStartDate, setEnrollmentStartDate] = useState<string>('');
  const [selectedAlternativeDates, setSelectedAlternativeDates] = useState<string[]>([]);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [enrollmentCondition, setEnrollmentCondition] = useState<'loading' | 'upcoming' | 'ongoing' | 'ended'>('loading');
  const [showEnrollmentSection, setShowEnrollmentSection] = useState<boolean>(false);
  const [optedOutItems, setOptedOutItems] = useState<string[]>([]);
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [waiverAccepted, setWaiverAccepted] = useState<boolean>(false);
  const [waiverSignatureName, setWaiverSignatureName] = useState<string>('');
  const [waiverSignatureDate, setWaiverSignatureDate] = useState<string>('');
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [campSessionSelections, setCampSessionSelections] = useState<Record<string, CampSelectionState>>({});
  const [posterImageAspect, setPosterImageAspect] = useState<'vertical' | 'wide' | 'normal' | null>(null);
  const [posterImageFailed, setPosterImageFailed] = useState(false);
  const sessionId = useMemo(() => uuidv4(), []);
  
  // Always determine if we're dealing with a slug or UUID
  const isSlugLookup = id && !isUUID(id) && looksLikeSlug(id);
  
  // Always compute the actual program ID
  const actualProgramId = useMemo(() => {
    if (!isSlugLookup) return id;
    return null; // Will be resolved by slug lookup
  }, [id, isSlugLookup]);

  // Always fetch all programs to maintain hook order
  const { data: allPrograms } = useQuery<ProgramForSlug[]>({
    queryKey: ['all-programs-for-slug'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('programs')
        .select('id, name')
        .eq('active', true);
      
      if (error) throw error;
      return data || [];
    },
    enabled: true, // Always enabled to maintain hook order
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000,   // Keep in cache for 10 minutes
  });

  // Always handle slug lookup and redirect
  useEffect(() => {
    if (isSlugLookup && allPrograms && id) {
      const program = findProgramBySlug(allPrograms, id);
      if (program) {
        // Redirect to the UUID-based URL
        navigate(`/programs/${program.id}`, { replace: true });
        return;
      }
    }
  }, [isSlugLookup, allPrograms, id, navigate]);

  const { data: program, isLoading, error } = useQuery<ProgramWithEnrollments>({
    queryKey: ['program', actualProgramId],
    queryFn: async () => {
      if (!actualProgramId) return null;
      
      const { data, error } = await supabase
        .from('programs')
        .select(`
          *,
          ${PROGRAM_LOCATION_EMBED},
                      enrollments (
              id,
              student_id,
              program_id,
              enrollment_start_date,
              enrollment_end_date,
              created_at,
              updated_at,
              is_active,
            students (
              id,
              first_name,
              last_name,
              date_of_birth
            ),
            invoices (
              id,
              total_amount,
              amount_paid,
              status,
              due_date
            )
          )
        `)
        .eq('id', actualProgramId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: true // Always enabled to maintain hook order
  });

  // Note: All early returns have been moved to the end to maintain consistent hook order

  const { data: lineItemConfigs, isLoading: isLineItemsLoading } = useQuery<PricingItem[]>({
    queryKey: ['program-line-item-configs', actualProgramId],
    queryFn: async () => {
      if (!actualProgramId) return [];
      
      const { data: rows, error } = await supabase
        .from('pricing')
        .select(`
          amount,
          is_optional,
          line_item_configs:line_item_config_id (
            id,
            name,
            type,
            description,
            subtype
          )
        `)
        .eq('program_id', actualProgramId);
      if (error) throw error;
      // Transform the data to match the PricingItem interface
      const items = (rows || []).map((row: DatabasePricingRow) => {
        const config = Array.isArray(row.line_item_configs)
          ? row.line_item_configs[0]
          : row.line_item_configs;
        if (!config) return null;
        return {
          amount: row.amount,
          is_optional: row.is_optional,
          line_item_config: {
            id: config.id,
            name: config.name,
            type: config.type ?? undefined,
            description: config.description,
            subtype: config.subtype ?? undefined
          }
        } as PricingItem;
      });
      return items.filter((item): item is PricingItem => item !== null);
    },
    enabled: true // Always enabled to maintain hook order
  });
  const pricingItems = useMemo(
    () => (Array.isArray(lineItemConfigs) ? lineItemConfigs : []),
    [lineItemConfigs]
  );

  // Every optional id this program is offering right now. Kept as its own value because the
  // enrollment stores the positive selection (`opt_in_items`) alongside the opt-outs, and the
  // positive set is only knowable against what was actually on offer at the time.
  const optionalItemIds = useMemo(
    () => pricingItems.filter(item => item.is_optional).map(item => item.line_item_config.id),
    [pricingItems],
  );

  // Opt-in model: default all optional items to opted-out (excluded from billing)
  useEffect(() => {
    if (pricingItems.length > 0) {
      setOptedOutItems(optionalItemIds);
    }
  }, [pricingItems, optionalItemIds]);

  const { data: parentData } = useQuery<{ id: string } | null>({
    queryKey: ['current-parent', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const { data } = await supabase
        .from('parents')
        .select('id')
        .eq('email', user.email)
        .single();
      return data;
    },
    enabled: true // Always enabled to maintain hook order
  });

  const campNudge = useUnpaidInvoiceNudge({ parentId: parentData?.id });

  // Multi-week bundle mode is a *summer* camp affordance: sibling weekly sessions, a Full
  // Summer Pass, per-week lunch. A one-off day or break camp shares `type = 'camp'` but has
  // none of that, so gate on the "<year> Summer Camp" tag rather than the type alone —
  // otherwise a single-day camp renders as a week picker and, worse, skips the BHCS-student
  // dual-price handling the standard pricing panel does.
  const summerCampTag = getSummerCampTag(program);
  const isWeeklyCampProgram = summerCampTag !== null && program?.billing_type === 'fixed_term';

  // A fixed-term series billed by the class (BHCS-152): the parent picks the first class, and
  // that pick — not "today" — is the start date the invoice bills from.
  const billedByClass = !!program && isBilledByClass(program) && !isWeeklyCampProgram;
  // Read again each time the modal opens, so a page left open past a class never offers it.
  const [firstClassNow, setFirstClassNow] = useState<BusinessNow>(() => businessNow());
  const firstClassChoices = useMemo(
    () => (billedByClass && program ? firstClassOptions(program, firstClassNow) : []),
    [billedByClass, program, firstClassNow],
  );

  // Fetch students for the parent
  const { data: allStudents, isLoading: isStudentsLoading } = useQuery<Student[]>({
    queryKey: ['parent-students', parentData?.id],
    queryFn: async () => {
      if (!parentData?.id) return [];
      const { data: studentParents } = await supabase
        .from('student_parents')
        .select('students (id, first_name, last_name, date_of_birth, active, metadata)')
        .eq('parent_id', parentData.id);
      return (studentParents || []).flatMap((sp: StudentParentRow) => sp.students);
    },
    enabled: !!parentData?.id, // Only enabled if parent data exists
  });

  // Fetch students already enrolled in this program. Soft-unenrolled rows are excluded so a
  // previously-unenrolled student can be picked again (EnrollStudent revives their row).
  const { data: enrolledStudentIds } = useQuery<string[]>({
    queryKey: ['enrolled-students', actualProgramId],
    queryFn: async () => {
      if (!actualProgramId) return [];
      const { data } = await supabase
        .from('enrollments')
        .select('student_id')
        .eq('program_id', actualProgramId)
        .eq('is_active', true);
      return (data || []).map((e: { student_id: string }) => e.student_id);
    },
    enabled: !!actualProgramId, // Always enabled for program details
  });

  // Filter students to only those not already enrolled and are active (only if parent data exists)
  const students = parentData ? allStudents?.filter((student) => {
    if (!student.active) return false;
    if (isWeeklyCampProgram) {
      // For bundle mode, do not hide students already enrolled in one week.
      return true;
    }
    return !enrolledStudentIds?.includes(student.id);
  }) : undefined;

  const parentStudentIds = useMemo(() => {
    return allStudents ? allStudents.map(s => s.id) : [];
  }, [allStudents]);

  // Capacity (BHCS-124). Inert for an uncapped program: no request, never full. When it is full,
  // only a child a seat is being held for can still enroll, so the picker narrows to them; the
  // database refuses anyone else regardless.
  const seats = useProgramSeats(program?.id, program?.capacity, { includeMine: !!parentData });
  const hasHeldSeat = seats.heldStudentIds.length > 0;
  const enrollableStudents = seats.isFull
    ? students?.filter((student) => seats.heldStudentIds.includes(student.id))
    : students;

  const enrollmentsToDisplay = useMemo(() => {
    if (!program?.enrollments) {
      return [];
    }

    // An admin/CLI unenroll soft-deactivates the row rather than deleting it, so the nested
    // select above still returns unenrolled students. Only currently-enrolled ones belong here.
    const activeEnrollments = program.enrollments.filter(enrollment => enrollment.is_active);

    if (isAdmin && !parentData) {
      return activeEnrollments;
    }

    // If parent data exists, filter to show only their students' enrollments
    if (parentData) {
      return activeEnrollments.filter(enrollment =>
        parentStudentIds.includes(enrollment.student_id)
      );
    }

    // If no parent data, don't show any enrollments
    return [];
  }, [program?.enrollments, parentStudentIds, isAdmin, parentData]);

  // Analytics for program view
  useEffect(() => {
    if (program && user) {
      trackEvent({
        eventType: 'program_view',
        user: user,
        entityType: 'program',
        entityId: program.id,
        sessionId: sessionId,
        metadata: { program_name: program.name },
        email: user.email,
      });
    }
  }, [program, user, id, sessionId]);

  useEffect(() => {
    if (program) {
      if (!program.start_date || !program.end_date) {
        setEnrollmentCondition('ended');
        setShowEnrollmentSection(false);
        setEnrollmentStartDate('');
        return;
      }
      // Billed by the class: open while a class can still be picked, and the first class is
      // preselected only when it is still ahead of today. Otherwise the parent chooses, so
      // whether today's class is included is always their decision.
      if (billedByClass) {
        const now = businessNow();
        const options = firstClassOptions(program, now);
        const allClasses = termClasses(program);
        const preselected = defaultFirstClass(options, allClasses, now.today);
        setEnrollmentCondition(options.length === 0 ? 'ended' : preselected ? 'upcoming' : 'ongoing');
        setShowEnrollmentSection(options.length > 0);
        setEnrollmentStartDate(preselected);
        return;
      }
      // Get today's date in UTC
      const today = new Date();
      const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
      const startUTC = new Date(Date.UTC(
        parseInt(program.start_date.split('-')[0]),
        parseInt(program.start_date.split('-')[1]) - 1,
        parseInt(program.start_date.split('-')[2])
      ));
      const endUTC = new Date(Date.UTC(
        parseInt(program.end_date.split('-')[0]),
        parseInt(program.end_date.split('-')[1]) - 1,
        parseInt(program.end_date.split('-')[2]),
        23, 59, 59
      ));

      // Format today's date in YYYY-MM-DD format for enrollment start date
      const todayStr = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}-${String(today.getUTCDate()).padStart(2, '0')}`;

      // For single-day programs or any program that has ended
      if (todayUTC > endUTC) {
        setEnrollmentCondition('ended');
        setShowEnrollmentSection(false);
        setEnrollmentStartDate('');
      } else if (todayUTC < startUTC) {
        setEnrollmentCondition('upcoming');
        setShowEnrollmentSection(true);
        setEnrollmentStartDate(program.start_date);
      } else {
        // Program is ongoing (today is between start and end dates inclusive)
        setEnrollmentCondition('ongoing');
        setShowEnrollmentSection(true);
        setEnrollmentStartDate(todayStr > program.start_date ? todayStr : program.start_date);
      }
    } else if (!isLoading && error) {
      setEnrollmentCondition('ended');
      setShowEnrollmentSection(false);
    }
  }, [program, isLoading, error, billedByClass]);

  // A first class that has since stopped being on offer (the page sat open past it) is cleared,
  // so the parent picks again rather than paying for a class that is over.
  useEffect(() => {
    if (billedByClass && enrollmentStartDate && !firstClassChoices.includes(enrollmentStartDate)) {
      setEnrollmentStartDate('');
    }
  }, [billedByClass, enrollmentStartDate, firstClassChoices]);

  // Program is running but closed to new signups. Parent-facing only — admins enroll through the
  // admin app, which is not gated by this (a separate RLS policy lets them through).
  const enrollmentLocked = !!program && isEnrollmentLocked(program);

  // A program belongs to one brand. Switching profiles while reading it would leave the other
  // brand's class under this brand's header, so the parent is returned to the catalog they
  // just switched into. A navigation, not an access rule — the page stays reachable by link.
  useBrandScopedPage(program?.brand_key, '/programs');
  // And the other direction: a shared link to this program opens on its brand.
  useLandingBrand(program?.brand_key);


  // The program's eligibility discount (BHCS Student or Foster City Resident), if it has one
  const eligibilityDiscount = findEligibilityDiscount(pricingItems.map(item => item.line_item_config.subtype));
  const isDiscountLine = (item: PricingItem) => isEligibilityDiscountSubtype(item.line_item_config.subtype);

  // Does the selected student qualify? Same rule as create_invoice_on_enrollment().
  const { data: studentQualifies } = useQuery({
    queryKey: ['discount-eligibility', eligibilityDiscount?.subtype, selectedStudentId],
    queryFn: () => fetchDiscountEligibility(supabase, eligibilityDiscount!.subtype, selectedStudentId!),
    enabled: !!selectedStudentId && !!eligibilityDiscount,
  });

  // Program page: always advertises the discount, since no student is in play yet.
  const detailsDiscount = resolveDiscountVisibility({
    hasDiscount: !!eligibilityDiscount,
    showBeforeStudentSelected: true,
    studentSelected: false,
    studentQualifies: undefined,
  });

  // Enrollment modal: advertises both prices until a student is picked, then quotes the
  // one that actually applies to them.
  const modalDiscount = resolveDiscountVisibility({
    hasDiscount: !!eligibilityDiscount,
    showBeforeStudentSelected: true,
    studentSelected: !!selectedStudentId,
    studentQualifies,
  });

  // Regular price excludes eligibility discount items
  const regularPrice = useMemo(() => {
    return pricingItems.reduce((sum, item) => {
      if (optedOutItems.includes(item.line_item_config.id)) return sum;
      if (isEligibilityDiscountSubtype(item.line_item_config.subtype)) return sum;
      return sum + item.amount;
    }, 0);
  }, [pricingItems, optedOutItems]);

  // Discounted price includes all items (including the discount)
  const discountedPrice = useMemo(() => {
    return pricingItems.reduce((sum, item) => {
      if (optedOutItems.includes(item.line_item_config.id)) return sum;
      return sum + item.amount;
    }, 0);
  }, [pricingItems, optedOutItems]);

  useEffect(() => {
    const baseTotal = pricingItems.reduce((sum, item) => {
      if (optedOutItems.includes(item.line_item_config.id)) {
        return sum;
      }
      // Exclude eligibility discounts from the base price used elsewhere
      if (isEligibilityDiscountSubtype(item.line_item_config.subtype)) return sum;
      return sum + item.amount;
    }, 0);
    setCurrentPrice(baseTotal);
  }, [pricingItems, optedOutItems]);

  const { data: campSessionProgramsData } = useQuery<CampSessionProgram[]>({
    queryKey: ['camp-session-programs', summerCampTag],
    queryFn: async () => {
      if (!summerCampTag) return [];
      const { data, error } = await supabase
        .from('programs')
        .select(`
          id,
          name,
          billing_type,
          start_date,
          end_date,
          schedule_cron,
          pricing (
            amount,
            is_optional,
            line_item_configs:line_item_config_id (
              id,
              name,
              type,
              description,
              subtype
            )
          )
        `)
        .eq('active', true)
        // A locked week is not offered in the bundle picker at all: selecting it would build an
        // enrollment the parent RLS policy then rejects, after they'd already chosen it.
        .eq('enrollment_status', 'open')
        .eq('type', 'camp')
        .eq('billing_type', 'fixed_term')
        // Same summer only: without this, an unrelated day camp that happens to be open
        // would be offered as one of this summer's weekly sessions.
        .contains('tags', [summerCampTag])
        .order('start_date', { ascending: true });
      if (error) throw error;
      const normalizedPrograms = (data ?? []).map((row) => {
        const pricingRows = Array.isArray(row.pricing) ? row.pricing : [];
        const pricing = pricingRows.flatMap((priceRow) => {
          const configs = Array.isArray(priceRow.line_item_configs)
            ? priceRow.line_item_configs
            : [priceRow.line_item_configs];
          const firstConfig = configs[0];
          if (!firstConfig) return [];
          return [{
            amount: priceRow.amount,
            is_optional: priceRow.is_optional,
            line_item_config: {
              id: firstConfig.id,
              name: firstConfig.name,
              type: firstConfig.type ?? undefined,
              description: firstConfig.description,
              subtype: firstConfig.subtype ?? undefined,
            },
          }];
        });
        return {
          id: row.id,
          name: row.name,
          billing_type: row.billing_type,
          start_date: row.start_date,
          end_date: row.end_date,
          schedule_cron: row.schedule_cron,
          pricing,
        } as CampSessionProgram;
      });
      return normalizedPrograms;
    },
    enabled: isWeeklyCampProgram,
  });

  const campSessionPrograms = useMemo(() => {
    if (!Array.isArray(campSessionProgramsData)) return [];
    return [...campSessionProgramsData].sort((a, b) => {
      const startDiff = (a.start_date ?? '').localeCompare(b.start_date ?? '');
      if (startDiff !== 0) return startDiff;
      // Same start date: longer programs (later end date) first
      return (b.end_date ?? '').localeCompare(a.end_date ?? '');
    });
  }, [campSessionProgramsData]);

  const selectedStudent = students?.find(s => s.id === selectedStudentId) || null;

  const { data: studentCampEnrollments } = useQuery<StudentCampEnrollmentRow[]>({
    queryKey: ['student-camp-enrollments', selectedStudentId, campSessionPrograms.map((p) => p.id).join(',')],
    queryFn: async () => {
      if (!selectedStudentId || campSessionPrograms.length === 0) return [];
      const { data, error } = await supabase
        .from('enrollments')
        .select('program_id')
        .eq('student_id', selectedStudentId)
        .in('program_id', campSessionPrograms.map((sessionProgram) => sessionProgram.id))
        .eq('is_active', true);
      if (error) throw error;
      return (data ?? []) as StudentCampEnrollmentRow[];
    },
    enabled: Boolean(selectedStudentId) && isWeeklyCampProgram,
  });

  const stableStudentCampEnrollments = studentCampEnrollments ?? EMPTY_STUDENT_CAMP_ENROLLMENTS;
  const alreadyEnrolledProgramIds = useMemo(() => {
    return new Set(stableStudentCampEnrollments.map((row) => row.program_id));
  }, [stableStudentCampEnrollments]);
  const alreadyEnrolledProgramIdsKey = useMemo(() => {
    return Array.from(alreadyEnrolledProgramIds).sort().join(',');
  }, [alreadyEnrolledProgramIds]);

  useEffect(() => {
    if (!isWeeklyCampProgram || campSessionPrograms.length === 0 || !program) {
      setCampSessionSelections({});
      return;
    }
    const nextSelections: Record<string, CampSelectionState> = {};
    campSessionPrograms.forEach((sessionProgram) => {
      const hasLunchOption = sessionProgram.pricing.some((item) => {
        const name = item.line_item_config.name.toLowerCase();
        const subtype = (item.line_item_config.subtype ?? '').toLowerCase();
        const type = (item.line_item_config.type ?? '').toLowerCase();
        return item.is_optional && (
          name.includes('lunch') ||
          subtype.includes('lunch') ||
          type.includes('lunch')
        );
      });
      nextSelections[sessionProgram.id] = {
        selected: sessionProgram.id === program.id && !alreadyEnrolledProgramIds.has(sessionProgram.id),
        lunchSelected: hasLunchOption,
      };
    });
    setCampSessionSelections((previous) => (
      areCampSelectionsEqual(previous, nextSelections) ? previous : nextSelections
    ));
  }, [alreadyEnrolledProgramIdsKey, campSessionPrograms, isWeeklyCampProgram, program]);

  const selectedCampSessionPrograms = useMemo(() => {
    return campSessionPrograms.filter((sessionProgram) => campSessionSelections[sessionProgram.id]?.selected);
  }, [campSessionPrograms, campSessionSelections]);

  const multiProgramSelections = useMemo(() => {
    if (!isWeeklyCampProgram) return [];
    return selectedCampSessionPrograms.map((sessionProgram) => {
      const lunchLineItemIds = sessionProgram.pricing
        .filter((item) => {
          const name = item.line_item_config.name.toLowerCase();
          const subtype = (item.line_item_config.subtype ?? '').toLowerCase();
          const type = (item.line_item_config.type ?? '').toLowerCase();
          return item.is_optional && (
            name.includes('lunch') ||
            subtype.includes('lunch') ||
            type.includes('lunch')
          );
        })
        .map((item) => item.line_item_config.id);
      const lunchSelected = campSessionSelections[sessionProgram.id]?.lunchSelected ?? false;
      return {
        programId: sessionProgram.id,
        enrollmentStartDate: sessionProgram.start_date,
        enrollmentEndDate: sessionProgram.end_date,
        optedOutItems: lunchSelected ? [] : lunchLineItemIds,
        optionalItemIds: sessionProgram.pricing
          .filter((item) => item.is_optional)
          .map((item) => item.line_item_config.id),
        metadata: {
          // Only record a lunch choice for a session that actually sells lunch. Stamping
          // `false` on a session with no lunch line item makes the enrollment claim the
          // parent declined an option that never existed, which EnrollmentDetails then
          // renders as "Lunch: Not included".
          ...(lunchLineItemIds.length > 0 ? { camp_lunch_selected: lunchSelected } : {}),
        },
      };
    }).filter((selection) => !alreadyEnrolledProgramIds.has(selection.programId));
  }, [alreadyEnrolledProgramIds, campSessionSelections, isWeeklyCampProgram, selectedCampSessionPrograms]);

  const isFullSummerPass = (name: string) => /full summer pass/i.test(name);

  const fullSummerPassSelected = useMemo(() => {
    return campSessionPrograms.some(
      (p) => isFullSummerPass(p.name) && (campSessionSelections[p.id]?.selected ?? false)
    );
  }, [campSessionPrograms, campSessionSelections]);

  const hasNonFullSummerPassSelected = useMemo(() => {
    return campSessionPrograms.some(
      (p) => !isFullSummerPass(p.name) && (campSessionSelections[p.id]?.selected ?? false)
    );
  }, [campSessionPrograms, campSessionSelections]);

  const bundleGrandTotal = useMemo(() => {
    return campSessionPrograms.reduce((grand, sessionProgram) => {
      if (!campSessionSelections[sessionProgram.id]?.selected) return grand;
      const lunchSelected = campSessionSelections[sessionProgram.id]?.lunchSelected ?? false;
      const lunchItem = sessionProgram.pricing.find((item) => {
        const name = item.line_item_config.name.toLowerCase();
        const subtype = (item.line_item_config.subtype ?? '').toLowerCase();
        const type = (item.line_item_config.type ?? '').toLowerCase();
        return item.is_optional && (name.includes('lunch') || subtype.includes('lunch') || type.includes('lunch'));
      });
      return grand + sessionProgram.pricing.reduce((sum, item) => {
        if (!lunchSelected && lunchItem && item.line_item_config.id === lunchItem.line_item_config.id) return sum;
        return sum + item.amount;
      }, 0);
    }, 0);
  }, [campSessionPrograms, campSessionSelections]);

  // Fetch eligible rules for discounts
  const { data: eligibleDiscounts } = useQuery<EligibleRule[]>({
    queryKey: ['eligible-discounts', program?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('eligible_rules')
        .select('*')
        .eq('active', true);
      if (error) throw error;
      return data || [];
    },
    enabled: true // Always enabled to maintain hook order
  });

  // Calculate final price and discount details
  const discountDetails: string[] = [];
  let totalDiscount = 0;
  // Calculate tuition base for percentage discounts
  const tuitionBase = pricingItems
    .filter((l: PricingItem) => l.line_item_config.type === 'tuition')
    .reduce((sum: number, l: PricingItem) => sum + l.amount, 0);
  if (program && program.billing_type === 'recurring_monthly' && selectedStudent && eligibleDiscounts) {
    let matchedDiscounts: EligibleRule[] = [];
    // School-based
    if (selectedStudent.metadata?.school) {
      matchedDiscounts = matchedDiscounts.concat(
        eligibleDiscounts.filter(
          (rule) => rule.type === 'school' && rule.value === selectedStudent.metadata?.school
        )
      );
    }
    // Student-based
    if (selectedStudent.id) {
      matchedDiscounts = matchedDiscounts.concat(
        eligibleDiscounts.filter(
          (rule: EligibleRule) => rule.type === 'student' && Array.isArray(JSON.parse(rule.value)) && JSON.parse(rule.value).includes(selectedStudent.id)
        )
      );
    }
    // Parent-based
    if (parentData?.id) {
      matchedDiscounts = matchedDiscounts.concat(
        eligibleDiscounts.filter(
          (rule: EligibleRule) => rule.type === 'parent' && Array.isArray(JSON.parse(rule.value)) && JSON.parse(rule.value).includes(parentData.id)
        )
      );
    }
    if (matchedDiscounts.length > 0) {
      matchedDiscounts.forEach((rule) => {
        if (rule.discount_type === 'percentage') {
          const discount = (tuitionBase * rule.discount_value) / 100;
          totalDiscount += discount;
          discountDetails.push(`-${discount.toLocaleString(undefined, { style: 'currency', currency: 'USD' })} (${rule.discount_value}% off${rule.type === 'school' ? ` for ${rule.value}` : ''}${rule.type === 'parent' ? ' (parent-based)' : ''}${rule.type === 'student' ? ' (student-based)' : ''})`);
        } else if (rule.discount_type === 'fixed_amount') {
          totalDiscount += rule.discount_value;
          discountDetails.push(`-$${rule.discount_value} off${rule.type === 'school' ? ` for ${rule.value}` : ''}${rule.type === 'parent' ? ' (parent-based)' : ''}${rule.type === 'student' ? ' (student-based)' : ''}`);
        }
      });
    }
  }

  // Update current price when discounts change
  useEffect(() => {
    const baseTotal = pricingItems.reduce((sum, item) => {
      if (optedOutItems.includes(item.line_item_config.id)) {
        return sum;
      }
      // Exclude eligibility discounts from the base price used elsewhere
      if (isEligibilityDiscountSubtype(item.line_item_config.subtype)) return sum;
      return sum + item.amount;
    }, 0);
    setCurrentPrice(baseTotal - totalDiscount);
  }, [pricingItems, optedOutItems, totalDiscount]);

  // Enrollment modal totals. Materials are billed as a separate one-off charge on
  // enrollment, so they're pulled out of the headline price and called out beneath it.
  const modalMaterialsTotal = (lineItemConfigs ?? [])
    .filter(item => item.line_item_config.type === 'materials' && !optedOutItems.includes(item.line_item_config.id))
    .reduce((sum, item) => sum + item.amount, 0);
  // Billed by the class: once a first class is picked, every non-materials line — discounts too —
  // is quoted for the classes from it to the end of the term, rounded per line exactly as the
  // invoice is, so the total here is the total billed.
  const firstClassShare = billedByClass && program && enrollmentStartDate ? classShare(program, enrollmentStartDate) : null;
  const proratedShare = firstClassShare && firstClassShare.billed < firstClassShare.total ? firstClassShare : null;
  const modalLineAmount = (item: PricingItem): number =>
    proratedShare && item.line_item_config.type !== 'materials' ? prorateAmount(item.amount, proratedShare) : item.amount;
  const proratedLinesTotal = (includeEligibilityDiscount: boolean) => pricingItems.reduce((sum, item) => {
    if (optedOutItems.includes(item.line_item_config.id)) return sum;
    if (item.line_item_config.type === 'materials') return sum;
    if (!includeEligibilityDiscount && isEligibilityDiscountSubtype(item.line_item_config.subtype)) return sum;
    return sum + modalLineAmount(item);
  }, 0);
  // currentPrice already excludes the eligibility discount; discountedPrice includes it.
  const modalRegularTotal = proratedShare ? proratedLinesTotal(false) : currentPrice - totalDiscount - modalMaterialsTotal;
  const modalDiscountedTotal = proratedShare ? proratedLinesTotal(true) : discountedPrice - totalDiscount - modalMaterialsTotal;
  /** "9 × $40.00" under a prorated line, so the arithmetic is on the screen. */
  const prorationNote = (item: PricingItem) =>
    proratedShare && item.line_item_config.type !== 'materials' ? (
      <span className="block text-[11px] font-normal text-gray-500">{describeProration(item.amount, proratedShare)}</span>
    ) : null;
  const modalPriceSuffix = program?.billing_type === 'per_day' || program?.billing_type === 'flex_monthly' ? '/day' : '';

  const handleEnrollClick = () => {
    // Belt-and-braces: the enroll button is already hidden for a locked program, but a stale
    // render (program locked while the page was open) must not open the modal — the INSERT would
    // be rejected by RLS anyway, after the parent had filled the whole form out.
    if (program && isEnrollmentLocked(program)) return;
    // Same for a program that filled while the page was open, unless a seat is held for them.
    if (seats.isFull && !hasHeldSeat) return;
    if (program && user) {
      trackEvent({
        eventType: 'program_enroll_start',
        user: user,
        entityType: 'program',
        entityId: program.id,
        sessionId: sessionId,
        metadata: { program_name: program.name },
        email: user.email,
      });
    }
    if (program) {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const d = String(now.getDate()).padStart(2, '0');
      setWaiverSignatureDate(`${y}-${m}-${d}`);
    }
    if (billedByClass) setFirstClassNow(businessNow());
    setIsEnrollModalOpen(true);
  };

  // Handle slug lookup states AFTER all hooks are called
  if (isSlugLookup) {
    // Show loading state while doing slug lookup
    if (!allPrograms) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Finding program...</p>
          </div>
        </div>
      );
    }

    // Show error if slug not found
    if (allPrograms && !findProgramBySlug(allPrograms, id || '')) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-gray-400 text-6xl mb-4">🔍</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Program Not Found</h2>
            <p className="text-gray-600 mb-4">
              The program "{id}" could not be found.
            </p>
            <button
              onClick={() => navigate('/programs')}
              className="brand-cta text-white px-4 py-2 rounded-lg transition-colors"
            >
              Browse All Programs
            </button>
          </div>
        </div>
      );
    }

    // If we're doing a slug lookup and have programs but no actualProgramId yet, show loading
    if (!actualProgramId) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Redirecting to program...</p>
          </div>
        </div>
      );
    }
  }

  // Using shared getStatusColor from types/status

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading program details...</p>
        </div>
      </div>
    );
  }

  if (error || !program) {
    return <div className="container mx-auto px-4 py-8">Error: Program not found</div>;
  }

  return (
    <>
    {campNudge.showBlocking && (
      <CampPaymentNudge invoices={campNudge.invoices} onDismiss={campNudge.dismiss} />
    )}
    <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8">
      {campNudge.showBanner && (
        <div className="mb-4">
          <CampPaymentNudge invoices={campNudge.invoices} variant="banner" onDismiss={campNudge.dismiss} />
        </div>
      )}
      <div className="mb-6">
        <Link
          to="/programs"
          className="text-[color:var(--brand-accent)] hover:text-[color:var(--brand-accent-ink)] flex items-center gap-2"
        >
          ← Back to Programs
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex items-start gap-3 sm:gap-4 mb-5 sm:mb-6">
          <span className="text-3xl sm:text-4xl flex-shrink-0 mt-0.5">{getProgramTypeIcon(program.type)}</span>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">{program.name}</h1>
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              <BrandBadge brand={program.brand_key} />
              <span className={`inline-flex text-xs font-medium px-2 py-0.5 rounded-full ${getProgramTypeColors(program.type)}`}>
                {programTypeName(program.type)}
              </span>
              <LocationPill name={programLocationName(program)} size="sm" />
            </div>
          </div>
        </div>
        
        {/* Program Description + Marketing: side-by-side when poster is vertical, stacked otherwise */}
        {(() => {
          const posterUrl = program.marketing_metadata?.poster_image;
          const websiteUrl = program.marketing_metadata?.website;
          const useSideBySide = posterUrl && !posterImageFailed && posterImageAspect === 'vertical' && program.description;

          const posterImg = posterUrl && !posterImageFailed ? (
            <img
              src={posterUrl}
              alt={`${program.name} poster`}
              className="rounded-lg shadow-md object-contain w-full"
              onLoad={(e) => {
                const img = e.currentTarget;
                const { naturalWidth: w, naturalHeight: h } = img;
                if (h > w) setPosterImageAspect('vertical');
                else if (w > h * 2) setPosterImageAspect('wide');
                else setPosterImageAspect('normal');
              }}
              onError={() => setPosterImageFailed(true)}
            />
          ) : null;

          const websiteLink = websiteUrl ? (
            <div className="mt-3 flex justify-center sm:justify-start">
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="brand-cta inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Visit Official Program Page
              </a>
            </div>
          ) : null;

          const descriptionBlock = program.description ? (
            <div
              className="rounded-lg shadow-md border border-[color:var(--brand-panel-edge)] p-4 sm:p-6 h-full"
              style={{ backgroundImage: 'var(--brand-panel)' }}
            >
              <h2 className="text-base sm:text-lg font-semibold mb-3 text-[color:var(--brand-panel-ink)]">{t('header.aboutProgram')}</h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {program.description}
                </p>
              </div>
            </div>
          ) : null;

          if (useSideBySide) {
            return (
              <div className="mb-6 flex flex-col sm:flex-row gap-4 sm:gap-6 items-start">
                <div className="w-full sm:w-auto sm:max-w-xs flex-shrink-0">
                  {posterImg}
                  {websiteLink}
                </div>
                <div className="flex-1">
                  {descriptionBlock}
                </div>
              </div>
            );
          }

          const posterWrapClass = posterImageAspect === 'wide' ? 'mb-3 w-full' : 'mb-3 max-w-lg mx-auto';
          return (
            <>
              {(posterImg || websiteLink) && (
                <div className="mb-6">
                  {posterImg && <div className={posterWrapClass}>{posterImg}</div>}
                  {websiteLink}
                </div>
              )}
              {descriptionBlock && <div className="mb-6">{descriptionBlock}</div>}
            </>
          );
        })()}
        
        <ProgramScheduleSummary
          program={program}
          className="mb-6"
          extraPills={program.invoice_due_date ? (
            <span className="inline-flex items-center text-xs bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-amber-800 font-medium">
              Payment due {formatDateDisplay(program.invoice_due_date)}
            </span>
          ) : null}
        />

        <div>
          <h2 className="text-base sm:text-lg font-semibold mb-3">{t('header.pricing')}</h2>
            {isLineItemsLoading ? (
              <div className="text-sm sm:text-base text-gray-500 mb-6">Loading pricing information...</div>
            ) : lineItemConfigs && lineItemConfigs.length > 0 ? (
                              <div className="divide-y divide-gray-200 bg-gray-50 rounded-lg p-3 sm:p-4">
                {/* Required items */}
                {lineItemConfigs.filter(item => !item.is_optional && !isEligibilityDiscountSubtype(item.line_item_config.subtype)).map((item: PricingItem) => (
                  <div key={item.line_item_config.id} className="py-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0 pr-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm sm:text-base font-medium text-gray-900">{item.line_item_config.name}</span>
                          {item.line_item_config.type && (
                            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 whitespace-nowrap">
                              {item.line_item_config.type}
                            </span>
                          )}
                        </div>
                        {item.line_item_config.description && (
                          <span className="block text-sm text-gray-500 mt-1">{item.line_item_config.description}</span>
                        )}
                      </div>
                      <div className="font-medium whitespace-nowrap text-gray-900">
                        ${item.amount.toFixed(2)}{((program.billing_type === 'per_day' && item.line_item_config.type === 'tuition') || (program.billing_type === 'flex_monthly' && isFlexDayCounted(item.line_item_config.type, item.line_item_config.subtype))) && '/day'}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Optional add-ons */}
                {lineItemConfigs.some(item => item.is_optional) && (
                  <>
                    <div className="py-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Optional Add-ons</span>
                    </div>
                    {lineItemConfigs.filter(item => item.is_optional).map((item: PricingItem) => (
                      <div key={item.line_item_config.id} className="py-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0 pr-4">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm sm:text-base font-medium text-gray-900">{item.line_item_config.name}</span>
                              {item.line_item_config.type && (
                                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 whitespace-nowrap">
                                  {item.line_item_config.type}
                                </span>
                              )}
                            </div>
                            {item.line_item_config.description && (
                              <span className="block text-sm text-gray-500 mt-1">{item.line_item_config.description}</span>
                            )}
                          </div>
                          <div className="font-medium whitespace-nowrap text-gray-500">
                            +${item.amount.toFixed(2)}{((program.billing_type === 'per_day' && item.line_item_config.type === 'tuition') || (program.billing_type === 'flex_monthly' && isFlexDayCounted(item.line_item_config.type, item.line_item_config.subtype))) && '/day'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* Eligibility discount line item (BHCS Student / Foster City Resident) */}
                {detailsDiscount.showDiscountLine && lineItemConfigs.filter(isDiscountLine).map((item: PricingItem) => (
                  <div key={item.line_item_config.id} className="py-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0 pr-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm sm:text-base font-medium text-green-700">{item.line_item_config.name}</span>
                          <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 whitespace-nowrap">
                            {eligibilityDiscount?.onlyLabel}
                          </span>
                        </div>
                        {item.line_item_config.description && (
                          <span className="block text-sm text-green-600 mt-1">{item.line_item_config.description}</span>
                        )}
                      </div>
                      <div className="font-medium whitespace-nowrap text-green-700">
                        -${Math.abs(item.amount).toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}

                <div className="py-3 flex flex-col">
                  {detailsDiscount.showDualPrices ? (
                    <>
                      <div className="flex justify-between items-center font-medium text-sm sm:text-base">
                        <span>Regular Price</span>
                        <div className="flex flex-col items-end">
                          <span>${(regularPrice - (lineItemConfigs?.filter(item => item.line_item_config.type === 'materials' && !isEligibilityDiscountSubtype(item.line_item_config.subtype)).reduce((sum, item) => sum + item.amount, 0) || 0)).toFixed(2)}{(program.billing_type === 'per_day' || program.billing_type === 'flex_monthly') && '/day'}</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center font-medium text-sm sm:text-base mt-1">
                        <span className="text-green-700">{eligibilityDiscount?.priceLabel}</span>
                        <div className="flex flex-col items-end">
                          <span className="text-green-700">${(discountedPrice - (lineItemConfigs?.filter(item => item.line_item_config.type === 'materials').reduce((sum, item) => sum + item.amount, 0) || 0)).toFixed(2)}{(program.billing_type === 'per_day' || program.billing_type === 'flex_monthly') && '/day'}</span>
                        </div>
                      </div>
                      {lineItemConfigs?.some(item => item.line_item_config.type === 'materials') && (
                        <div className="text-xs sm:text-sm text-gray-600 text-right mt-1">
                          + ${lineItemConfigs.filter(item => item.line_item_config.type === 'materials').reduce((sum, item) => sum + item.amount, 0).toFixed(2)} one-off charge on enrollment
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex justify-between items-center font-medium text-sm sm:text-base">
                      <span>Total</span>
                      <div className="flex flex-col items-end">
                        <span>${(regularPrice - (lineItemConfigs?.filter(item => item.line_item_config.type === 'materials').reduce((sum, item) => sum + item.amount, 0) || 0)).toFixed(2)}{(program.billing_type === 'per_day' || program.billing_type === 'flex_monthly') && '/day'}</span>
                        {lineItemConfigs?.some(item => item.line_item_config.type === 'materials') && (
                          <span className="text-xs sm:text-sm text-gray-600">
                            + ${lineItemConfigs.filter(item => item.line_item_config.type === 'materials').reduce((sum, item) => sum + item.amount, 0).toFixed(2)} one-off charge on enrollment
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm sm:text-base text-gray-500 mb-6">No pricing information configured for this program.</div>
            )}
        </div>

        {/* Camp refund policy — summer camps only; break/day camps have no such policy */}
        {isSummerCampProgram(program) && (
          <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-amber-900">Cancellation & refund policy</p>
            <ul className="mt-2 list-disc pl-5 text-sm text-amber-800 space-y-1">
              {SUMMER_CAMP_POLICY.cancellationTiers.map((tier) => (
                <li key={tier}>{tier}</li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-amber-700">
              Questions: <span className="font-medium">{SUMMER_CAMP_POLICY.supportEmail}</span>
            </p>
          </div>
        )}

        {/* Enrollment section - only show if parent data exists.
            Order matters: no-parent first (onboarding beats everything), then the date window,
            then the admin lock, so a program that has genuinely ended still says so rather than
            reporting the less specific "enrollment closed". */}
        {parentData && showEnrollmentSection && !enrollmentLocked && seats.isFull && !hasHeldSeat ? (
          <ProgramWaitlist
            programId={program.id}
            programName={program.name}
            mode="full"
            seats={seats}
            allChildren={allStudents ?? []}
            eligibleChildren={students ?? []}
          />
        ) : parentData && showEnrollmentSection && !enrollmentLocked ? (
          <>
            <ProgramWaitlist
              programId={program.id}
              programName={program.name}
              mode="open"
              seats={seats}
              allChildren={allStudents ?? []}
              eligibleChildren={students ?? []}
            />
            <div className="mt-8">
              <div className="flex justify-end">
                <button
                  className="brand-cta px-4 py-2 text-white rounded-[var(--brand-btn-radius)] transition-colors"
                  onClick={handleEnrollClick}
                >
                  {t('action.enroll')}
                </button>
              </div>
            </div>
          </>
        ) : parentData && showEnrollmentSection && enrollmentLocked ? (
          <div className="mt-8">
            <div className="p-4 bg-amber-50 border-l-4 border-amber-400 rounded-md">
              <p className="text-sm sm:text-base text-amber-900">
                {t('program.enrollmentClosedNotice')}
              </p>
            </div>
          </div>
        ) : !parentData ? (
          <div className="mt-8">
            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg shadow-lg">
              <div className="text-sm sm:text-base text-blue-800 space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">🎉</span>
                  <p className="font-semibold text-lg">Welcome aboard! Let's get you set up!</p>
                </div>
                <p className="text-blue-700">Hey there! We're excited to have you join our BHCS family. Here's the quick scoop on getting started:</p>
                <ol className="list-decimal list-inside space-y-2 ml-4 text-blue-700">
                  <li><Link to="/onboarding" className="underline hover:text-blue-900 font-medium">Complete your parent profile setup</Link> - it's like filling out a digital permission slip, but way cooler! 📝</li>
                  <li>Add your little ones through the Students page - we'll give them a quick once-over and have them ready to roll in no time! ⚡</li>
                </ol>
                <p className="text-blue-600 italic mt-3">Don't worry, we promise this is the only paperwork you'll need to do! 😄</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-8">
            <div className="p-4 bg-gray-50 border-l-4 border-gray-400 rounded-md">
              <p className="text-sm sm:text-base text-gray-700">
                {billedByClass
                  ? 'Enrollment is closed: there are no classes left in this program.'
                  : <>Enrollment is not available for this program as it has ended on {formatDateDisplay(program.end_date)}.</>}
              </p>
            </div>
          </div>
        )}

        {/* Enrollment Modal */}
        <Modal
          isOpen={isEnrollModalOpen}
          onClose={() => setIsEnrollModalOpen(false)}
          title={isWeeklyCampProgram ? `${summerCampTag} — Select Sessions` : program.name}
        >
          <div className="space-y-6">
            {/* Pricing Details Section */}
            {!isWeeklyCampProgram ? (
              isLineItemsLoading ? (
              <div className="mb-6">Loading line item configs...</div>
            ) : lineItemConfigs && lineItemConfigs.length > 0 ? (
              <div>
                <h3 className={`text-sm sm:text-base font-medium text-gray-900 ${billedByClass ? 'mb-1' : 'mb-4'}`}>{t('header.pricingDetails')}</h3>
                {billedByClass && (
                  <p className="text-xs text-gray-500 mb-3" data-testid="class-billing-note">
                    {proratedShare
                      ? `For ${proratedShare.billed} of ${proratedShare.total} classes, from ${formatWeekdayDateDisplay(enrollmentStartDate)}.`
                      : firstClassShare
                        ? `For all ${firstClassShare.total} classes.`
                        : 'Priced for the full term. Pick the first class below to see your price.'}
                  </p>
                )}
                <div className="divide-y divide-gray-200 bg-gray-50 rounded-lg p-3 sm:p-4">
                  {/* Required items */}
                  {lineItemConfigs.filter(item => !item.is_optional && !isEligibilityDiscountSubtype(item.line_item_config.subtype)).map((item: PricingItem) => (
                    <div key={item.line_item_config.id} className="py-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0 pr-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm sm:text-base font-medium text-gray-900">{item.line_item_config.name}</span>
                            {item.line_item_config.type && (
                              <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 whitespace-nowrap">
                                {item.line_item_config.type}
                                {item.line_item_config.type === 'materials' && ' (one-off charge on enrollment)'}
                              </span>
                            )}
                          </div>
                          {item.line_item_config.description && (
                            <span className="block text-xs text-gray-500 mt-1">{item.line_item_config.description}</span>
                          )}
                        </div>
                        <div className="font-medium whitespace-nowrap text-gray-900 text-right">
                          {modalLineAmount(item) < 0 ? '-' : ''}${Math.abs(modalLineAmount(item)).toFixed(2)}{((program.billing_type === 'per_day' && item.line_item_config.type === 'tuition') || (program.billing_type === 'flex_monthly' && isFlexDayCounted(item.line_item_config.type, item.line_item_config.subtype))) && '/day'}
                          {prorationNote(item)}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Optional add-ons */}
                  {lineItemConfigs.some(item => item.is_optional) && (
                    <div className="py-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Optional Add-ons</span>
                    </div>
                  )}
                  {lineItemConfigs.filter(item => item.is_optional).map((item: PricingItem) => (
                    <div key={item.line_item_config.id} className="py-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0 pr-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm sm:text-base font-medium text-gray-900">{item.line_item_config.name}</span>
                            {item.line_item_config.type && (
                              <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 whitespace-nowrap">
                                {item.line_item_config.type}
                                {item.line_item_config.type === 'materials' && ' (one-off charge on enrollment)'}
                              </span>
                            )}
                          </div>
                          {item.line_item_config.description && (
                            <span className="block text-xs text-gray-500 mt-1">{item.line_item_config.description}</span>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <label className="inline-flex items-center whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                              checked={!optedOutItems.includes(item.line_item_config.id)}
                              onChange={(e) => {
                                const newOptedOutItems = e.target.checked
                                  ? optedOutItems.filter(id => id !== item.line_item_config.id)
                                  : [...optedOutItems, item.line_item_config.id];
                                setOptedOutItems(newOptedOutItems);
                              }}
                            />
                            <span className="ml-2 text-xs sm:text-sm text-gray-600">Include</span>
                          </label>
                          <div className={`font-medium whitespace-nowrap text-right ${
                            optedOutItems.includes(item.line_item_config.id)
                              ? 'text-gray-400'
                              : 'text-gray-900'
                          }`}>
                            ${modalLineAmount(item).toFixed(2)}{((program.billing_type === 'per_day' && item.line_item_config.type === 'tuition') || (program.billing_type === 'flex_monthly' && isFlexDayCounted(item.line_item_config.type, item.line_item_config.subtype))) && '/day'}
                            {prorationNote(item)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Eligibility discount line item in modal */}
                  {modalDiscount.showDiscountLine && lineItemConfigs.filter(isDiscountLine).map((item: PricingItem) => (
                    <div key={item.line_item_config.id} className="py-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0 pr-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm sm:text-base font-medium text-green-700">{item.line_item_config.name}</span>
                            <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 whitespace-nowrap">
                              {eligibilityDiscount?.onlyLabel}
                            </span>
                          </div>
                          {item.line_item_config.description && (
                            <span className="block text-xs text-green-600 mt-1">{item.line_item_config.description}</span>
                          )}
                        </div>
                        <div className="font-medium whitespace-nowrap text-green-700 text-right">
                          -${Math.abs(modalLineAmount(item)).toFixed(2)}
                          {prorationNote(item)}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Display discount details if applicable */}
                  {selectedStudent && discountDetails.length > 0 && (
                    <div className="py-3">
                      {discountDetails.map((desc, idx) => (
                        <div key={idx} className="flex items-center justify-between py-1 text-red-700">
                          <div>
                            <span className="font-medium">Discount</span>
                          </div>
                          <div className="font-medium">{desc}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="py-3 flex flex-col">
                    {modalDiscount.showDualPrices ? (
                      /* No student picked yet — show both prices so the discount is visible. */
                      <>
                        <div className="flex justify-between items-center font-medium text-sm sm:text-base">
                          <span>Regular Price</span>
                          <div className="flex flex-col items-end">
                            <span>${modalRegularTotal.toFixed(2)}{modalPriceSuffix}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center font-medium text-sm sm:text-base mt-1">
                          <span className="text-green-700">{eligibilityDiscount?.priceLabel}</span>
                          <div className="flex flex-col items-end">
                            <span className="text-green-700">${modalDiscountedTotal.toFixed(2)}{modalPriceSuffix}</span>
                          </div>
                        </div>
                        {modalMaterialsTotal > 0 && (
                          <div className="text-xs sm:text-sm text-gray-600 text-right mt-1">
                            + ${modalMaterialsTotal.toFixed(2)} one-off charge on enrollment
                          </div>
                        )}
                      </>
                    ) : (
                      /* A student is picked (or the discount can't apply) — show only the
                         one price that actually applies to this enrollment. */
                      <>
                        <div className="flex justify-between items-center font-medium text-sm sm:text-base">
                          <span className={modalDiscount.applyDiscountToTotal ? 'text-green-700' : ''}>Total</span>
                          <div className="flex flex-col items-end">
                            <span className={modalDiscount.applyDiscountToTotal ? 'text-green-700' : ''}>
                              ${(modalDiscount.applyDiscountToTotal ? modalDiscountedTotal : modalRegularTotal).toFixed(2)}{modalPriceSuffix}
                            </span>
                            {modalMaterialsTotal > 0 && (
                              <span className="text-xs sm:text-sm text-gray-600">
                                + ${modalMaterialsTotal.toFixed(2)} one-off charge on enrollment
                              </span>
                            )}
                          </div>
                        </div>
                        {modalDiscount.applyDiscountToTotal && (
                          <div className="text-xs mt-1 flex items-center justify-end gap-1 text-green-600">
                            <span>{eligibilityDiscount?.appliedLabel}</span>
                            <span className="relative group">
                              <Info className="inline-block w-3.5 h-3.5 flex-shrink-0 cursor-help" />
                              <span className="absolute bottom-full right-0 mb-1 hidden group-hover:block w-56 px-2 py-1.5 text-xs text-white bg-gray-800 rounded shadow-lg z-10 font-normal text-left leading-relaxed">
                                {eligibilityDiscount?.rule}
                              </span>
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm sm:text-base text-gray-500 mb-6">No pricing line items configured for this program.</div>
            )) : (
              <div className="mb-6">
                <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-3">{t('header.selectWeeklySessions')}</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-4">
                  Choose one or more weekly sessions. Lunch is mapped per selected session.
                </p>
                <div className="space-y-3">
                  {campSessionPrograms.map((sessionProgram) => {
                    const lunchItem = sessionProgram.pricing.find((item) => {
                      const name = item.line_item_config.name.toLowerCase();
                      const subtype = (item.line_item_config.subtype ?? '').toLowerCase();
                      const type = (item.line_item_config.type ?? '').toLowerCase();
                      return item.is_optional && (
                        name.includes('lunch') ||
                        subtype.includes('lunch') ||
                        type.includes('lunch')
                      );
                    });
                    const isSelected = campSessionSelections[sessionProgram.id]?.selected ?? false;
                    const lunchSelected = campSessionSelections[sessionProgram.id]?.lunchSelected ?? false;
                    const isAlreadyEnrolled = alreadyEnrolledProgramIds.has(sessionProgram.id);
                    const tuitionTotal = sessionProgram.pricing
                      .filter((item) => item.line_item_config.type === 'tuition')
                      .reduce((sum, item) => sum + item.amount, 0);
                    const effectiveTotal = sessionProgram.pricing.reduce((sum, item) => {
                      if (!lunchSelected && lunchItem && item.line_item_config.id === lunchItem.line_item_config.id) return sum;
                      return sum + item.amount;
                    }, 0);
                    const isThisFullSummerPass = isFullSummerPass(sessionProgram.name);
                    const isDisabledByExclusivity = !isThisFullSummerPass && fullSummerPassSelected
                      || isThisFullSummerPass && hasNonFullSummerPassSelected;
                    return (
                      <div key={sessionProgram.id} className={`rounded-lg border p-3 ${isAlreadyEnrolled ? 'border-green-300 bg-green-50' : isDisabledByExclusivity ? 'border-gray-200 bg-gray-100 opacity-50' : 'border-gray-200 bg-gray-50'}`}>
                        <label className={`flex items-start gap-3 ${isDisabledByExclusivity ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300"
                            checked={isSelected}
                            disabled={isAlreadyEnrolled || isDisabledByExclusivity}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setCampSessionSelections((previous) => ({
                                ...previous,
                                [sessionProgram.id]: {
                                  selected: checked,
                                  lunchSelected: previous[sessionProgram.id]?.lunchSelected ?? Boolean(lunchItem),
                                },
                              }));
                            }}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {sessionProgram.name}
                              {isAlreadyEnrolled && (
                                <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">
                                  Already enrolled
                                </span>
                              )}
                            </p>
                            <p className="text-[11px] text-gray-600 mt-0.5">
                              {formatDateDisplay(sessionProgram.start_date)} - {formatDateDisplay(sessionProgram.end_date)} · {getScheduleTimeRange(sessionProgram.schedule_cron)}
                            </p>
                            <div className="mt-1 text-[11px] text-gray-700 space-y-0.5">
                              <div className="flex justify-between">
                                <span>Tuition</span>
                                <span>${tuitionTotal.toFixed(2)}</span>
                              </div>
                              {sessionProgram.pricing
                                .filter((item) => item.line_item_config.type !== 'tuition' && item.line_item_config.type !== 'materials' && (!lunchItem || item.line_item_config.id !== lunchItem.line_item_config.id))
                                .map((item) => (
                                  <div key={item.line_item_config.id} className="flex justify-between text-gray-500">
                                    <span>{item.line_item_config.name}</span>
                                    <span>${item.amount.toFixed(2)}</span>
                                  </div>
                                ))}
                              {sessionProgram.pricing
                                .filter((item) => item.line_item_config.type === 'materials')
                                .map((item) => (
                                  <div key={item.line_item_config.id} className="flex justify-between text-gray-500">
                                    <span>{item.line_item_config.name} <span className="text-gray-400">(one-off)</span></span>
                                    <span>${item.amount.toFixed(2)}</span>
                                  </div>
                                ))}
                              {lunchItem && !isSelected && (
                                <div className="flex justify-between text-gray-500">
                                  <span>Lunch option</span>
                                  <span>+${lunchItem.amount.toFixed(2)}</span>
                                </div>
                              )}
                              <div className="flex justify-between font-medium border-t border-gray-200 pt-0.5 mt-0.5">
                                <span>Total</span>
                                <span>${effectiveTotal.toFixed(2)}</span>
                              </div>
                            </div>
                            {lunchItem && isSelected && (
                              <label className="inline-flex items-center mt-2 text-xs text-gray-700">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                  checked={lunchSelected}
                                  disabled={isAlreadyEnrolled}
                                  onChange={(event) => {
                                    const checked = event.target.checked;
                                    setCampSessionSelections((previous) => ({
                                      ...previous,
                                      [sessionProgram.id]: {
                                        selected: true,
                                        lunchSelected: checked,
                                      },
                                    }));
                                  }}
                                />
                                <span className="ml-2">
                                  Include lunch ({lunchItem.amount >= 0 ? `+$${lunchItem.amount.toFixed(2)}` : `-$${Math.abs(lunchItem.amount).toFixed(2)}`})
                                </span>
                              </label>
                            )}
                            {isAlreadyEnrolled && (
                              <p className="mt-2 text-[11px] text-green-700">
                                An enrollment record already exists for this week, so it cannot be added again.
                              </p>
                            )}
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
                {(fullSummerPassSelected || hasNonFullSummerPassSelected) && (
                  <p className="mt-2 text-xs text-amber-700">
                    Full Summer Pass cannot be combined with individual weekly sessions.
                  </p>
                )}
                {selectedCampSessionPrograms.length > 0 && (
                  <div className="mt-3 flex justify-between items-center text-sm font-semibold text-gray-900 border-t border-gray-200 pt-3">
                    <span>Selected sessions total</span>
                    <span>${bundleGrandTotal.toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}

            {program.invoice_due_date && (
              <div className="text-sm text-gray-600">
                Payment due by <span className="font-medium text-amber-700">{formatDateDisplay(program.invoice_due_date)}</span>
              </div>
            )}

            <div className="border-t border-gray-200 pt-6">
              {/* Student Selection Dropdown */}
              {!parentData ? (
                <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-md">
                  <div className="text-sm text-blue-700 space-y-2">
                    <p><strong>Note:</strong> To enroll students in this program:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-4">
                      <li><Link to="/onboarding" className="underline hover:text-blue-800">Complete your parent profile setup</Link> - establish your account</li>
                      <li>Contact billing@bhcskids.com - to add your students to the system</li>
                    </ol>
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  <label htmlFor="student-select" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Select Student</label>
                  <select
                    id="student-select"
                    className="w-full border rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    value={selectedStudentId ?? ''}
                    onChange={e => setSelectedStudentId(e.target.value || null)}
                    disabled={isStudentsLoading || !enrollableStudents?.length}
                  >
                    <option value="">-- Select a student --</option>
                    {enrollableStudents?.map(student => (
                      <option key={student.id} value={student.id}>
                        {student.first_name} {student.last_name} (DOB: {student.date_of_birth ? formatDateDisplay(student.date_of_birth) : 'Not provided'})
                      </option>
                    ))}
                  </select>
                  {Array.isArray(program.levels) && program.levels.length > 0 && (
                    <div className="mt-4">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Select Level</label>
                      <select
                        className="w-full border rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        value={selectedLevel ?? ''}
                        onChange={(e) => setSelectedLevel(e.target.value || '')}
                      >
                        <option value="">-- Select a level --</option>
                        {program.levels.map((lvl) => (
                          <option key={lvl} value={lvl}>{lvl}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Billed by the class: the parent picks the first class, which is the start date. */}
              {billedByClass && (
                <FirstClassPicker
                  program={program}
                  options={firstClassChoices}
                  value={enrollmentStartDate}
                  onChange={setEnrollmentStartDate}
                  today={firstClassNow.today}
                />
              )}

              {/* Enrollment Start Date Selector (only for ongoing programs) */}
              {!billedByClass && enrollmentCondition === 'ongoing' && (
                <div className="mb-4">
                  <label htmlFor="enrollment-start-date" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Enrollment Start Date
                  </label>
                  <input
                    type="date"
                    id="enrollment-start-date"
                    name="enrollment-start-date"
                    value={enrollmentStartDate}
                    min={program.start_date > getTodayDateString() ? program.start_date : getTodayDateString()}
                    max={program.end_date}
                    onChange={(e) => setEnrollmentStartDate(e.target.value)}
                    className="w-full border rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Select a start date between {formatDateDisplay(program.start_date > getTodayDateString() ? program.start_date : getTodayDateString())} and program end ({formatDateDisplay(program.end_date)}).
                  </p>
                </div>
              )}

              {/* For upcoming programs, display the fixed start date */}
              {!billedByClass && enrollmentCondition === 'upcoming' && (
                <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-400 text-blue-700 rounded-md">
                  <p className="text-xs sm:text-sm">
                    {isWeeklyCampProgram
                      ? 'Enrollment will be effective from the program start date.'
                      : <>This program starts on: <strong>{formatDateDisplay(program.start_date)}</strong>.<br />Enrollment will be effective from this date.</>
                    }
                  </p>
                </div>
              )}

              {/* Alternative Start Date Options - Only for recurring_monthly programs */}
              {enrollmentCondition === 'upcoming' && program.billing_type === 'recurring_monthly' && program.alternative_dates && program.alternative_dates.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-900 mb-3">Alternative Start Dates</h4>
                  <div className="space-y-2">
                    {program.alternative_dates.map((altDate, index) => {
                      // Validate the alternative date is within program bounds
                      const altDateObj = new Date(altDate.date);
                      const startDateObj = new Date(program.start_date);
                      const endDateObj = new Date(program.end_date);
                      
                      // Only show alternative dates that are between program start and end dates
                      if (altDateObj < startDateObj || altDateObj > endDateObj) {
                        return null;
                      }

                      return (
                        <div key={index} className="border border-orange-200 bg-orange-50 rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <div className="flex items-center justify-center w-6 h-6 bg-orange-100 rounded-full">
                                <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center">
                                <label className="flex items-start cursor-pointer group">
                                  <input
                                    type="checkbox"
                                    className="form-checkbox h-4 w-4 text-orange-600 rounded border-gray-300 focus:ring-orange-500 mt-1 mr-3"
                                    checked={selectedAlternativeDates.includes(altDate.date)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedAlternativeDates(prev => [...prev, altDate.date]);
                                      } else {
                                        setSelectedAlternativeDates(prev => prev.filter(date => date !== altDate.date));
                                      }
                                    }}
                                  />
                                  <div>
                                                            <span className="text-xs sm:text-sm font-medium text-gray-900 group-hover:text-orange-700">
                          Alternative Start Date <strong className="text-orange-700">{formatDateDisplay(altDate.date)}</strong>
                        </span>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1">
                          {altDate.notes}
                        </p>
                                  </div>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Day Selection for per_day programs */}
              {program.billing_type === 'per_day' && (
                <div className="mb-4">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-900 mb-3">{t('header.selectDays')}</h4>
                  <p className="text-xs sm:text-sm text-gray-600 mb-4">
                    Choose the specific days you want your child to attend. You'll be charged ${(currentPrice - (lineItemConfigs?.filter(item => item.line_item_config.type === 'materials').reduce((sum, item) => sum + item.amount, 0) || 0)).toFixed(2)}/day per day selected.
                  </p>
                  <DaySelector
                    startDate={program.start_date}
                    endDate={program.end_date}
                    selectedDays={selectedDays}
                    onChange={setSelectedDays}
                    minSelections={1}
                    scheduleCron={program.schedule_cron}
                    isSelectableDate={sessionDayPredicate(program)}
                  />
                </div>
              )}

              {/* Waiver/Disclaimer Section (moved lower, above enroll) */}
              {program.waiver && (
                <div className="mt-4">
                  <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-2">Waiver & Acknowledgement</h3>
                  <div className="p-3 sm:p-4 bg-gray-50 border rounded text-sm text-gray-800 whitespace-pre-wrap">
                    {program.waiver}
                  </div>
                  <div className="mt-3 flex items-start gap-2">
                    <input
                      id="waiver-accept"
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                      checked={waiverAccepted}
                      onChange={(e) => setWaiverAccepted(e.target.checked)}
                    />
                    <label htmlFor="waiver-accept" className="text-xs sm:text-sm text-gray-700">
                      I have read and agree to the waiver/disclaimer.
                    </label>
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Parent/Guardian Full Name (Signature)</label>
                      <input
                        type="text"
                        className="w-full border rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        value={waiverSignatureName}
                        onChange={(e) => setWaiverSignatureName(e.target.value)}
                        placeholder="Type your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Signature Date</label>
                      <input
                        type="date"
                        className="w-full border rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        value={waiverSignatureDate}
                        onChange={(e) => setWaiverSignatureDate(e.target.value)}
                        max={program.end_date}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* EnrollStudent Component */}
              {parentData && (
                <div className="mt-6">
                  <EnrollStudent 
                    programId={program.id} 
                    selectedStudentId={selectedStudentId} 
                    enrollmentStartDate={
                      // Use selected alternative date only for recurring_monthly programs
                      program.billing_type === 'recurring_monthly' && selectedAlternativeDates.length > 0 
                        ? selectedAlternativeDates[0]
                        : program.billing_type === 'per_day' && selectedDays.length > 0 
                          ? selectedDays[0] 
                          : enrollmentStartDate
                    }
                    enrollmentEndDate={program.billing_type === 'per_day' && selectedDays.length > 0 
                      ? selectedDays[selectedDays.length - 1] 
                      : program.end_date}
                    selectedDays={program.billing_type === 'per_day' ? selectedDays : undefined}
                    billingType={program.billing_type}
                    optedOutItems={optedOutItems}
                    optionalItemIds={optionalItemIds}
                    sessionId={sessionId}
                    programName={program.name}
                    programType={program.type}
                    programSchedule={getScheduleTimeRange(program.schedule_cron)}
                    scheduleCron={program.schedule_cron}
                    brandKey={program.brand_key}
                    requireWaiver={!!program.waiver}
                    waiverSignature={{ name: waiverSignatureName, date: waiverSignatureDate }}
                    availableLevels={Array.isArray(program.levels) ? program.levels : undefined}
                    selectedLevel={selectedLevel}
                    multiProgramSelections={isWeeklyCampProgram ? multiProgramSelections : undefined}
                    onSuccess={() => {
                      setIsEnrollModalOpen(false);
                      setSelectedStudentId(null);
                      setSelectedDays([]);
                      setSelectedAlternativeDates([]);
                      setWaiverAccepted(false);
                      setWaiverSignatureName('');
                      setWaiverSignatureDate('');
                      setCampSessionSelections({});
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </Modal>

        {/* Enrolled Students Section - only show if parent data exists */}
        {parentData && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h2 className="text-base sm:text-lg font-semibold mb-4">{t('header.enrolledStudents')}</h2>
            {enrollmentsToDisplay && enrollmentsToDisplay.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {enrollmentsToDisplay.map((enrollment) => {
                  const invoice = enrollment.invoices?.[0];
                  const amountDue = invoice ? invoice.total_amount - invoice.amount_paid : 0;
                  // Provide program price context to payment status helper to differentiate free vs refunded $0
                  const paymentStatus = getEnrollmentPaymentStatus(
                    { ...enrollment, programs: { price: program.price } },
                    enrollment.invoices,
                    cartHeldInvoiceIds
                  );
                  return (
                    <div 
                      key={enrollment.id} 
                      className="bg-gray-50 p-4 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => navigate(`/enrollments/${enrollment.id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {enrollment.students.first_name} {enrollment.students.last_name}
                          </h3>
                          <p className="text-xs sm:text-sm text-gray-600">
                            Enrolled: {formatDateDisplay(enrollment.enrollment_start_date)} - {formatDateDisplay(enrollment.enrollment_end_date)}
                          </p>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="text-sm font-medium text-gray-900">
                            ${amountDue.toFixed(2)}
                          </span>
                          <span className={`mt-1 px-2 py-1 rounded text-xs font-medium ${getStatusColor(paymentStatus)}`}>
                            {paymentStatus}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm sm:text-base text-gray-500 text-center py-4">You have no students enrolled in this program yet.</p>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
}
